<template><div><code-well-header>
  <div class="d-d-flex d-jc-center">
    <example-toast show title="Title" class="d-ps-relative d-zi-base d-t0" :fixed="false" />
  </div>
</code-well-header>
<!-- <component-combinator component-name="DtToast" /> -->
<h2 id="variants-and-examples" tabindex="-1" class="d-docsite--header-2">Variants and Examples <a class="header-anchor d-link d-docsite--link d-link" href="#variants-and-examples" aria-hidden="true">#</a></h2>
<code-well-header>
    <div class="d-d-flex d-w100p d-flow8 d-ai-flex-end">
        <div class="d-fl-grow1">
            <dt-select-menu label="Style" :options="toastOptions" @change="changeKind" />
        </div>
        <dt-checkbox value="important" @input="toggleImportant">Important</dt-checkbox>
        <dt-button @click="toggleToast">Toggle Example</dt-button>
    </div>
</code-well-header>
<example-toast
  class="d-zi-notification"
  :show="showToast"
  title="Title"
  :important="important"
  :kind="selectedKind"
  @close="toggleToast"
/>
<code-example-tabs
htmlCode='
<aside class="d-toast-wrapper">
  <div class="d-toast d-toast--base" role="status" aria-hidden="true">
    <div class="d-toast__dialog">
      <div class="d-notice__icon">...</div>
      <div class="d-notice__content">
        <h2 class="d-notice__title">...</h2>
        <p class="d-notice__message">...</p>
      </div>
      <div class="d-notice__actions">...</div>
    </div>
  </div>
</aside>
<aside class="d-toast-wrapper">
  <div class="d-toast d-toast--error" role="status" aria-hidden="true">...</div>
</aside>
<aside class="d-toast-wrapper">
  <div class="d-toast d-toast--info" role="status" aria-hidden="true">...</div>
</aside>
<aside class="d-toast-wrapper">
  <div class="d-toast d-toast--success" role="status" aria-hidden="true">...</div>
</aside>
<aside class="d-toast-wrapper">
  <div class="d-toast d-toast--warning" role="status" aria-hidden="true">...</div>
</aside>
'
vueCode='
<dt-toast
  title="Title"
  :show="showToast"
  :important="important"
  :kind="selectedKind"
  @close="closeEvent"
>
  Message body with
  <dt-link>
    a link
  </dt-link>
  <template #action>
    <dt-button
      size="sm"
      importance="outlined"
      kind="muted"
    >
      Action
    </dt-button>
  </template>
</dt-toast>
'
showHtmlWarning />
<h3 id="with-duration" tabindex="-1" class="d-docsite--header-3">With Duration <a class="header-anchor d-link d-docsite--link d-link" href="#with-duration" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">It's recommended to use a time of at least 6000 ms (minimum duration validated in the component) to give users enough time to read the toast. Take into account that the time necessary to read and comprehend the message could vary in users. For instance, users using assistive technology, or users with language barriers could potentially need more time to read and understand the message.
If the duration is not provided the toast won't disappear automatically.</p>
<code-well-header>
  <dt-button @click="toggleDurationToast(true)">Show Example</dt-button>
  <example-toast
    class="d-zi-notification"
    :show="showDurationToast"
    title="Title"
    @close="toggleDurationToast(false)"
    @update:show="updateShow"
    :duration="6000"
  />
</code-well-header>
<code-example-tabs
vueCode='
<dt-toast
  title="Title"
  :show="showDurationToast"
  @close="closeEvent"
  :duration="7500"
>
  Message body with
  <dt-link>
    a link
  </dt-link>
  <template #action>
    <dt-button
      size="sm"
      importance="outlined"
      kind="muted"
    >
      Action
    </dt-button>
  </template>
</dt-toast>
'
/>
<h3 id="with-self-positioning" tabindex="-1" class="d-docsite--header-3">With Self-Positioning <a class="header-anchor d-link d-docsite--link d-link" href="#with-self-positioning" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">If you need to self-position the toast at the top center, use the <code v-pre>d-toast-wrapper</code> Dialtone class:</p>
<div class="language-html" data-highlighter="prismjs" data-ext="html"><pre v-pre><code class="language-html"><span class="line"><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>aside</span> <span class="token attr-name">class</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>d-toast-wrapper<span class="token punctuation">"</span></span><span class="token punctuation">></span></span></span>
<span class="line">  <span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>dt-toast</span></span>
<span class="line">    <span class="token attr-name">:title</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>title<span class="token punctuation">"</span></span></span>
<span class="line">    <span class="token attr-name">:message</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>message<span class="token punctuation">"</span></span></span>
<span class="line">    <span class="token attr-name">:show</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>isShown<span class="token punctuation">"</span></span></span>
<span class="line">  <span class="token punctuation">></span></span><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;/</span>dt-toast</span><span class="token punctuation">></span></span></span>
<span class="line"><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;/</span>aside</span><span class="token punctuation">></span></span></span>
<span class="line"></span></code></pre>
</div><h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="toast" /><h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<component-class-table component-name="toast" /><h2 id="accessibility" tabindex="-1" class="d-docsite--header-2">Accessibility <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Avoid using toast for critical information since toast disappears automatically and make sure
to provide enough time to read the message and act consequently. For best accessible user experience, the amount of
time a toast displays for should be user configurable.</p>
<p class="d-docsite--paragraph">Using <code v-pre>role=&quot;alert&quot;</code>, it sets <code v-pre>aria-live=&quot;assertive&quot;</code> which
means it will immediately interrupt anything currently being read by the screen reader, so use it for things
that require immediate attention such as:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">An invalid value was entered into a form field</li>
<li class="d-docsite--list-element">The user's login session is about to expire</li>
<li class="d-docsite--list-element">The connection to the server was lost, local changes will not be saved</li>
</ul>
<p class="d-docsite--paragraph">Meanwhile <code v-pre>role=&quot;status&quot;</code> implies <code v-pre>aria-live=&quot;polite&quot;</code> which
means the toast will be read out after what's currently being has finished.</p>
<p class="d-docsite--paragraph">A screen reader visible only close button is added by default.</p>
<component-accessible-table component-name="toast" /></div></template>


<script setup>
import ExampleToast from '@exampleComponents/ExampleToast.vue';
import { ref } from 'vue';

const toastOptions = [
  { value: 'base', label: 'Base' },
  { value: 'error', label: 'Error' },
  { value: 'info', label: 'Info' },
  { value: 'success', label: 'Success' },
  { value: 'warning', label: 'Warning' },
];
const showToast = ref(false);
const important = ref(false);
const pinned = ref(false);
const selectedKind = ref('base');
const showDurationToast = ref(false);

function toggleToast () {
  showToast.value = !showToast.value;
}
function toggleDurationToast (value) {
  showDurationToast.value = value;
}
const updateShow = (value) => {
  if (!value) showDurationToast.value = false;
};

function toggleImportant () {
  important.value = !important.value;
}
function togglePinned () {
  pinned.value = !pinned.value;
}
function changeKind (kind) {
  selectedKind.value = kind;
}
</script>